import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FinishedEventPayload,
  LIVE_TRANSCRIPTION_FALLBACK_POLL_INTERVAL_MS,
  LIVE_TRANSCRIPTION_SEGMENTS_LIMIT,
  LiveTranscriptionState,
  LiveTranscriptSegment,
  SegmentEventPayload,
  UseLiveTranscriptionInput,
} from 'src/components/Assistant/live-transcript/live-transcript.types';
import {
  buildSegmentKey,
  mergeSegmentsByOrder,
} from 'src/components/Assistant/live-transcript/live-transcript.utils';
import { wsEventBus } from 'src/core/realtime-connection';
import { useConversationLiveTranscription } from 'src/graphql';

const EMPTY_STATE: LiveTranscriptionState = {
  segments: [],
  isStreaming: false,
  isFinished: false,
  transcriptionId: null,
  externalTranscriptionId: null,
};

export const useLiveTranscription = ({
  enabled,
  conversationId,
  partnerId,
}: UseLiveTranscriptionInput): LiveTranscriptionState => {
  const [loadLiveTranscription] = useConversationLiveTranscription();
  const scopeKey = `${enabled ? '1' : '0'}:${conversationId ?? ''}:${partnerId ?? ''}`;
  const [scopedState, setScopedState] = useState<{
    scopeKey: string;
    value: LiveTranscriptionState;
  }>({
    scopeKey,
    value: EMPTY_STATE,
  });

  const cursorRef = useRef<string | null>(null);
  const seenSegmentsRef = useRef<Set<string>>(new Set());
  const isPendingRef = useRef(false);
  const fallbackIntervalRef = useRef<number | null>(null);

  const state = scopedState.scopeKey === scopeKey ? scopedState.value : EMPTY_STATE;

  const setStateForScope = useCallback(
    (
      updater:
        | LiveTranscriptionState
        | ((previous: LiveTranscriptionState) => LiveTranscriptionState),
    ) => {
      setScopedState((previousScopedState) => {
        const previousState =
          previousScopedState.scopeKey === scopeKey ? previousScopedState.value : EMPTY_STATE;
        const nextState = typeof updater === 'function' ? updater(previousState) : updater;

        return {
          scopeKey,
          value: nextState,
        };
      });
    },
    [scopeKey],
  );

  useEffect(() => {
    const hasConversation = Boolean(conversationId);
    const hasPartner = partnerId !== null && partnerId !== undefined;

    if (!enabled || !hasConversation || !hasPartner) {
      return;
    }

    let isActive = true;

    const stopFallbackPolling = () => {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };

    const hydrateFromQuery = async (input: { useCursor: boolean }): Promise<boolean> => {
      if (!isActive || isPendingRef.current) {
        return false;
      }

      isPendingRef.current = true;

      try {
        const { data } = await loadLiveTranscription({
          variables: {
            params: {
              conversationId: conversationId!,
              partnerId,
              limit: LIVE_TRANSCRIPTION_SEGMENTS_LIMIT,
              direction: 'FORWARD',
              cursor: input.useCursor ? cursorRef.current : null,
            },
          },
        });

        if (!isActive) {
          return false;
        }

        const payload = data?.getConversationLiveTranscription;

        if (!payload) {
          setStateForScope((previous) => {
            if (!previous.transcriptionId) {
              return EMPTY_STATE;
            }

            return {
              ...previous,
              isStreaming: false,
              isFinished: true,
            };
          });

          return false;
        }

        const mappedSegments: LiveTranscriptSegment[] = [];

        for (const segment of payload.segments) {
          const segmentKey = buildSegmentKey({
            externalTranscriptionId: payload.externalTranscriptionId,
            timestamp: segment.timestamp,
            role: segment.role,
            transcriptionText: segment.transcriptionText,
          });

          if (seenSegmentsRef.current.has(segmentKey)) {
            continue;
          }

          seenSegmentsRef.current.add(segmentKey);
          mappedSegments.push({
            id: segmentKey,
            role: segment.role,
            timestamp: segment.timestamp,
            transcriptionText: segment.transcriptionText,
            start: segment.start ?? undefined,
            duration: segment.duration ?? undefined,
          });
        }

        setStateForScope((previous) => {
          const isSameTranscription =
            previous.externalTranscriptionId === null ||
            previous.externalTranscriptionId === payload.externalTranscriptionId;
          const baseSegments = isSameTranscription ? previous.segments : [];
          const isFinished =
            payload.isFinished || (isSameTranscription && previous.isFinished);

          return {
            segments: mergeSegmentsByOrder(baseSegments, mappedSegments),
            isStreaming: !isFinished,
            isFinished,
            transcriptionId: payload.transcriptionId,
            externalTranscriptionId: payload.externalTranscriptionId,
          };
        });

        cursorRef.current = payload.cursor ?? null;

        return true;
      } catch {
        return false;
      } finally {
        isPendingRef.current = false;
      }
    };

    const startFallbackPolling = () => {
      if (fallbackIntervalRef.current !== null) {
        return;
      }

      fallbackIntervalRef.current = window.setInterval(() => {
        void hydrateFromQuery({ useCursor: true });
      }, LIVE_TRANSCRIPTION_FALLBACK_POLL_INTERVAL_MS);
    };

    // Custom transcription events arrive as { data: { type, ...payload } } with no `operation`
    // field, so onOperation() cannot be used here — we must filter on data.type directly.
    const segmentSub = wsEventBus.asObservable().subscribe({
      next: (rawEvent) => {
        const segmentEvent = rawEvent.data as unknown as SegmentEventPayload | undefined;

        if (segmentEvent?.type !== 'RealTimeTranscriptionSegment') {
          return;
        }

        if (
          segmentEvent.conversationId !== conversationId ||
          segmentEvent.partnerId !== partnerId
        ) {
          return;
        }

        const segmentKey = buildSegmentKey({
          externalTranscriptionId: segmentEvent.externalTranscriptionId,
          timestamp: segmentEvent.timestamp,
          role: segmentEvent.role,
          transcriptionText: segmentEvent.transcriptionText,
        });

        if (seenSegmentsRef.current.has(segmentKey)) {
          return;
        }

        seenSegmentsRef.current.add(segmentKey);

        const nextSegment: LiveTranscriptSegment = {
          id: segmentKey,
          role: segmentEvent.role,
          timestamp: segmentEvent.timestamp,
          transcriptionText: segmentEvent.transcriptionText,
          start: segmentEvent.start,
          duration: segmentEvent.duration,
        };

        setStateForScope((previous) => {
          const isNewTranscription =
            previous.externalTranscriptionId !== null &&
            previous.externalTranscriptionId !== segmentEvent.externalTranscriptionId;

          const baseSegments = isNewTranscription ? [] : previous.segments;

          return {
            segments: mergeSegmentsByOrder(baseSegments, [nextSegment]),
            isStreaming: true,
            isFinished: false,
            transcriptionId: segmentEvent.transcriptionId,
            externalTranscriptionId: segmentEvent.externalTranscriptionId,
          };
        });
      },
      error: (err: unknown) => console.error('WS RealTimeTranscriptionSegment error:', err),
    });

    const finishedSub = wsEventBus.asObservable().subscribe({
      next: (rawEvent) => {
        const finishedEvent = rawEvent.data as unknown as FinishedEventPayload | undefined;

        if (finishedEvent?.type !== 'RealTimeTranscriptionFinished') {
          return;
        }

        if (
          finishedEvent.conversationId !== conversationId ||
          finishedEvent.partnerId !== partnerId
        ) {
          return;
        }

        setStateForScope((previous) => {
          if (
            previous.externalTranscriptionId &&
            finishedEvent.externalTranscriptionId &&
            previous.externalTranscriptionId !== finishedEvent.externalTranscriptionId
          ) {
            return previous;
          }

          return {
            ...previous,
            isStreaming: false,
            isFinished: true,
            transcriptionId: finishedEvent.transcriptionId,
            externalTranscriptionId:
              finishedEvent.externalTranscriptionId ?? previous.externalTranscriptionId,
          };
        });

        stopFallbackPolling();
      },
      error: (err: unknown) => console.error('WS RealTimeTranscriptionFinished error:', err),
    });

    const disconnectSub = wsEventBus.onDisconnect().subscribe(() => {
      startFallbackPolling();
      void hydrateFromQuery({ useCursor: true });
    });

    const reconnectSub = wsEventBus.onReconnect().subscribe(() => {
      stopFallbackPolling();
      void hydrateFromQuery({ useCursor: true });
    });

    const subs = [segmentSub, finishedSub, disconnectSub, reconnectSub];

    void (async () => {
      let hasMore = await hydrateFromQuery({ useCursor: false });
      while (isActive && hasMore && cursorRef.current) {
        hasMore = await hydrateFromQuery({ useCursor: true });
      }
    })();

    return () => {
      isActive = false;
      stopFallbackPolling();
      cursorRef.current = null;
      seenSegmentsRef.current = new Set();
      isPendingRef.current = false;
      subs.forEach((s) => s.unsubscribe());
      setStateForScope(EMPTY_STATE);
    };
  }, [conversationId, enabled, loadLiveTranscription, partnerId, scopeKey, setStateForScope]);

  return state;
};
